import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * A wrapper component that protects routes requiring authentication.
 * Redirects to sign-in page if user is not authenticated.
 *
 * Optional `requireFeature` (KYN-499): the name of a feature flag that must be
 * enabled in the user's resolved featureFlags (from the backend resolver). If it
 * is not enabled, the user is redirected home. This is a UX guard only — the
 * backend independently enforces the same gate on the API, so a missing flag here
 * is never the sole line of defense.
 */
const ProtectedRoute = ({ children, requireFeature }) => {
  const { isAuthenticated, authChecked, user } = useAuth();
  const location = useLocation();

  // Wait for auth check to complete before rendering
  if (!authChecked) {
    // Show loading state while checking authentication
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // If not authenticated, redirect to sign-in with return URL
  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  // If this route is feature-gated, require the flag to be enabled. Fail closed:
  // an absent featureFlags object (old/missing response) keeps the feature hidden.
  if (requireFeature && !user?.featureFlags?.[requireFeature]) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated (and feature-enabled, if required), render the content
  return children;
};

export default ProtectedRoute;
